/**
 * @fileoverview LocationCell — renders the Location column cell for both
 * {@link ListGroupedView} and {@link MasterGroupedView}.
 *
 * Displays, on a single line:  `City, State`
 * And on a second line (when available): the formatted address.
 *
 * This component extracts duplicated location-rendering logic so that changes
 * to the Location column format only need to happen in one place.
 */

import * as React from 'react';
import { Box, Typography } from '@mui/material';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Props for {@link LocationCell}. */
export interface LocationCellProps {
    /** City name. May be null/undefined if not available. */
    city?: string | null;
    /** State abbreviation. May be null/undefined. */
    state?: string | null;
    /** Full formatted address from Google Places (e.g. "123 Main St, Nashville, TN 37201, USA"). */
    formattedAddress?: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders a two-line location display:
 * - Line 1: `City, State` (or `—` when both are absent).
 * - Line 2: `formattedAddress` (omitted when not available).
 *
 * @param props.city             - City name.
 * @param props.state            - State abbreviation.
 * @param props.formattedAddress - Full formatted address from Google Places.
 *
 * @example
 * <LocationCell city="Nashville" state="TN" formattedAddress="100 Broadway, Nashville, TN 37201, USA" />
 */
const LocationCell: React.FC<LocationCellProps> = ({ city, state, formattedAddress }) => {
    const cityState = [city, state].filter(Boolean).join(', ') || '—';

    return (
        <Box>
            <Typography variant="body2" component="span" display="block" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                {cityState}
            </Typography>
            {formattedAddress && (
                <Typography variant="caption" color="text.disabled" display="block" noWrap title={formattedAddress}>
                    {formattedAddress}
                </Typography>
            )}
        </Box>
    );
};

export default LocationCell;
